import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient, InfiniteData } from '@tanstack/react-query';
import { useCallback } from 'react';
import { api } from '../api';
import type { Avatar, Paginated, Session } from '../api';

type HistoryPage = Paginated<Avatar, string>;
type HistoryCache = InfiniteData<HistoryPage>;

/**
 * Pure cache writer for the owner side of avatar lifecycle. Routes
 * the row into the right slot:
 *
 *   - deleted (deletedAt set) → drop from ['avatar','me'] (defensive)
 *     and from ['avatar','history']
 *   - alive  → ['avatar','me']; remove from history if it had landed
 *     there earlier
 *   - ended  → clear ['avatar','me'] if it matched, prepend/replace in
 *     ['avatar','history']
 *
 * Navigation is **not** done here — pages that care subscribe to the
 * relevant cache slice via useEffect and decide based on their own
 * context (route, ids they're showing). Keeps data layer pure and
 * avoids yanking users away from pages they don't care about.
 *
 * Idempotent: same avatar can be processed twice with no extra effect.
 */
export function processMyAvatar(qc: QueryClient, avatar: Avatar) {
  processMyAvatarList(qc, [avatar]);
}

/**
 * Batched flavor — apply N avatar updates with a single setQueryData
 * per cache key (`['avatar','me']` and `['avatar','history']`).
 *
 * Per-row routing matches the single version: deleted → drop from both
 * slots; alive → seat into `me`, scrub from history; ended → clear
 * `me` if matched, upsert into history (update in place if already
 * present, otherwise prepend to first page).
 *
 * History stays sorted by createdAt desc (avatar.createdAt never
 * mutates, so order is stable across upserts).
 */
export function processMyAvatarList(qc: QueryClient, avatars: Avatar[]) {
  if (avatars.length === 0) return;

  // ── ['avatar','me'] — last write wins across the batch ──────────
  qc.setQueryData<Avatar | null>(['avatar', 'me'], (old) => {
    let me = old ?? null;
    for (const avatar of avatars) {
      if (avatar.deletedAt) {
        if (me?.id === avatar.id) me = null;
        continue;
      }
      const isAlive = !avatar.expiredAt;
      if (isAlive) { me = avatar; continue; }
      // ended: clear if it was my live slot
      if (me?.id === avatar.id) me = null;
    }
    return me;
  });

  // ── ['avatar','history'] — flatten / apply / sort / put back ────
  qc.setQueryData<HistoryCache>(['avatar', 'history'], (old) => {
    if (!old?.pages.length) return old;
    let all = old.pages.flatMap(p => p.items);
    let touched = false;
    for (const avatar of avatars) {
      const i = all.findIndex(a => a.id === avatar.id);
      if (avatar.deletedAt) {
        if (i >= 0) { all = all.filter(a => a.id !== avatar.id); touched = true; }
        continue;
      }
      const isAlive = !avatar.expiredAt;
      if (isAlive) {
        // alive avatars don't belong in history; scrub if it had
        // landed there earlier.
        if (i >= 0) { all = all.filter(a => a.id !== avatar.id); touched = true; }
        continue;
      }
      // ended: upsert.
      if (i >= 0) all = all.map((a, j) => (j === i ? avatar : a));
      else        all = [avatar, ...all];
      touched = true;
    }
    if (!touched) return old;
    // Stable order: history is createdAt desc. createdAt is immutable,
    // so we only re-sort if something was added/updated (cheap).
    all = [...all].sort((a, b) => b.createdAt - a.createdAt);
    const [first, ...rest] = old.pages;
    return {
      ...old,
      pages: [
        { ...first, items: all },
        ...rest.map(p => ({ ...p, items: [] as Avatar[] })),
      ],
    };
  });
}

/**
 * Hook flavor — returns a stable bound `(avatar) => processMyAvatar(qc, avatar)`.
 */
export function useProcessMyAvatar() {
  const qc = useQueryClient();
  return useCallback((avatar: Avatar) => processMyAvatar(qc, avatar), [qc]);
}

export function useProcessMyAvatarList() {
  const qc = useQueryClient();
  return useCallback((avatars: Avatar[]) => processMyAvatarList(qc, avatars), [qc]);
}

/**
 * Broadcast handler for `reloadAvatar`. Fired by the BE when an avatar
 * is explicitly ended (cron-driven natural expiry stays silent —
 * detected on each device by the global tick reaper). The payload
 * carries the lifecycle stamps so each receiver does its job locally
 * with no API round-trip:
 *
 *   1) Drop from the feed cache, if present.
 *   2) If the ended avatar matches `['avatar','me']`, route via
 *      processMyAvatar so it moves to history and clears the slot.
 *   3) Stamp `session.expiredAt` (and the embedded `session.avatar`
 *      lifecycle fields) on every cached session referencing this
 *      avatarId — both the `['sessions','mine']` slots and every
 *      per-id `['sessions', id]`.
 *
 * Idempotent: each step is a no-op if its target is already in the
 * post-state, so the broadcast can hit the originating tab too with
 * no harm.
 */
type FeedPage = { items: Array<{ id: string }>; next: string | null; prev: string | null };
type FeedCache = { pages: FeedPage[]; pageParams?: unknown[] } | undefined;
type SessionsPage = { items: Session[]; next: string | null; prev: string | null };
type SessionsCache = { pages: SessionsPage[]; pageParams?: unknown[] } | undefined;

export function processAvatar(qc: QueryClient, payload: {
  avatarId: string;
  expiredAt: number;
  endedAt: number | null;
}) {
  const { avatarId, expiredAt, endedAt } = payload;

  // 1) feed — paginated envelope; drop this avatar's row from each
  //    page's `items`.
  qc.setQueryData<FeedCache>(['feed'], (old) => {
    if (!old) return old;
    let changed = false;
    const pages = old.pages.map(p => {
      const items = p.items.filter(a => a.id !== avatarId);
      if (items.length === p.items.length) return p;
      changed = true;
      return { ...p, items };
    });
    return changed ? { ...old, pages } : old;
  });

  // 2) my own avatar slot — route via processMyAvatar so the row
  //    follows the same alive/ended/deleted pipeline as a regular
  //    `reloadYourAvatar`.
  const me = qc.getQueryData<Avatar | null>(['avatar', 'me']);
  if (me?.id === avatarId) {
    processMyAvatar(qc, { ...me, expiredAt, endedAt });
  }

  // 3) sessions referencing this avatar — both seeker and avatar
  //    inboxes (now two separate paginated caches), plus every per-id
  //    session cache entry. Mirrors the BE clamp on expire/end:
  //    `lastReadAt = lastMessageAt` so an expired row doesn't carry a
  //    stuck unread dot. Without this, the row falling out of the
  //    /messages top-20 silently flips the tab badge off while the BE
  //    has clamped — divergent state.
  const stampSession = (s: Session): Session => {
    if (s.avatarId !== avatarId) return s;
    return {
      ...s,
      expiredAt: s.expiredAt ?? expiredAt,
      lastReadAt: s.lastMessageAt,
      avatar: s.avatar
        ? { ...s.avatar, expiredAt, endedAt: endedAt ?? s.avatar.endedAt ?? null }
        : s.avatar,
    };
  };
  const stampSessionsCache = (old: SessionsCache): SessionsCache => {
    if (!old?.pages.length) return old;
    let changed = false;
    const pages = old.pages.map(p => {
      const items = p.items.map(stampSession);
      if (items.some((s, i) => s !== p.items[i])) {
        changed = true;
        return { ...p, items };
      }
      return p;
    });
    return changed ? { ...old, pages } : old;
  };
  qc.setQueryData<SessionsCache>(['sessions', 'mine', 'seeker'], stampSessionsCache);
  qc.setQueryData<SessionsCache>(['sessions', 'mine', 'avatar'], stampSessionsCache);
  qc.getQueryCache().findAll({
    predicate: (q) => {
      const k = q.queryKey;
      return Array.isArray(k) && k.length === 2 && k[0] === 'sessions'
        && typeof k[1] === 'string' && k[1] !== 'mine' && k[1] !== 'open';
    },
  }).forEach((q) => {
    const data = q.state.data as Session | undefined;
    if (!data || data.avatarId !== avatarId) return;
    qc.setQueryData<Session>(q.queryKey, stampSession(data));
  });
}

export function useMyAvatar() {
  return useQuery({ queryKey: ['avatar', 'me'], queryFn: api.avatars.me, staleTime: 30_000 });
}

export function useAvatarHistory() {
  return useInfiniteQuery({
    queryKey: ['avatar', 'history'],
    queryFn: ({ pageParam }) => api.avatars.history({ next: pageParam }),
    initialPageParam: undefined as string | undefined,
    // BE returns `next` ready-to-use as the cursor for the page after
    // this one (older items). Null means "no more older".
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    staleTime: 60_000,
  });
}

export function useAvatarSessions(avatarId: string) {
  return useInfiniteQuery({
    queryKey: ['avatar', avatarId, 'sessions'],
    queryFn:  ({ pageParam }) => api.avatars.sessions(avatarId, { next: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    staleTime: 60_000,
    enabled:  !!avatarId,
  });
}

export function useCreateAvatar() {
  const qc = useQueryClient();
  const handle = useProcessMyAvatar();
  return useMutation({
    mutationFn: api.avatars.create,
    onSuccess: (created) => {
      handle(created);
      // Sessions list is now scoped to this avatar server-side; clear
      // the cache so the operations room doesn't briefly show inherited
      // chats from a previous avatar.
      qc.invalidateQueries({ queryKey: ['sessions', 'mine'] });
    },
  });
}

export function useDeactivateAvatar() {
  const qc = useQueryClient();
  const handle = useProcessMyAvatar();
  return useMutation({
    mutationFn: api.avatars.deactivate,
    onSuccess: (ended) => {
      if (ended) handle(ended);
      else qc.setQueryData(['avatar', 'me'], null);
    },
  });
}

/**
 * Permanently delete an archived avatar from the user's memories.
 * BE stamps deletedAt and returns the row; processMyAvatar handles
 * the cache cleanup and the redirect away from /memory/<id> if we're
 * sitting on that page.
 */
export function useDeleteAvatar() {
  const handle = useProcessMyAvatar();
  return useMutation({
    mutationFn: (avatarId: string) => api.avatars.delete(avatarId),
    onSuccess: (deleted) => handle(deleted),
  });
}
